import React from "react";
import LocationLifeInsurancePage from "./LocationLifeInsurancePage.jsx";

const calgaryData = {
  slug: "calgary",
  city: "Calgary",
  seo: {
    title: "Life Insurance Calgary | Licensed Broker | einsured.ca",
    description:
      "Compare life insurance in Calgary from Canada's top insurers with licensed broker support for term, permanent, mortgage, and no medical life insurance.",
    keywords:
      "Life Insurance in Calgary, Calgary life insurance, Alberta life insurance, life insurance broker Calgary, life insurance quotes Calgary",
  },
  hero: {
    title: "Life Insurance in Calgary",
    subtitle: "Find the Right Plan Without the Pressure",
    description:
      "Compare plans from Canada's top insurers. A licensed broker who works for you, not any single company.",
  },
  intro: {
    heading: "Calgary Has a High Cost of Living. Your Life Insurance Should Reflect That.",
    paragraphs: [
      "According to the Calgary Real Estate Board, average residential prices have climbed past $600,000 and families in communities like Tuscany, Auburn Bay, and Signal Hill often carry mortgages well above that. Add childcare, vehicle costs, and two incomes running one household and the financial exposure is significant.",
      "Life insurance in Calgary isn't about worst-case thinking. It's about making sure your family keeps the home, the stability, and the standard of living they depend on - even if you're no longer there to provide it.",
      "At einsured.ca, we help Calgary and Alberta residents find the right coverage, from the right insurer, at the right price. Fully online. No waiting rooms.",
    ],
  },
  underinsured: {
    heading: "Why Calgary Families Can't Afford to Be Underinsured?",
    lead: "Getting life insurance in Calgary right means understanding what your household actually depends on and where the gaps usually hide.",
    points: [
      {
        title: "Group plans aren't a full solution.",
        description:
          "Employer coverage feels reassuring until you leave the job. Laid off, resigned, or retired early - that coverage disappears. Even while active, group plans rarely account for Calgary's mortgage reality or the full years of income your family depends on.",
      },
      {
        title: "Alberta mortgage debt is substantial.",
        description:
          "Many Calgary households carry $500,000 to $800,000 in mortgage debt alone. Alberta life insurance coverage that doesn't account for that balance isn't real protection - it's a gap dressed up as a safety net.",
      },
    ],
    question:
      "The honest question: if you passed away today, could your family stay in their home and maintain their standard of living without your income? If the answer is uncertain, this is worth addressing now.",
  },
  broker: {
    heading: "What a Life Insurance Broker in Calgary Actually Does?",
    description:
      "There's a real difference between an agent and a broker. An agent represents one company. A broker represents you. At einsured.ca, we’re not tied to any single insurer. That means every recommendation is focused on getting you the right coverage at the best possible price.",
    cards: [
      {
        title: "Unbiased comparison",
        description:
          "We pull life insurance quotes in Calgary from multiple top-rated carriers and walk you through the differences clearly.",
      },
      {
        title: "Needs-based planning",
        description:
          "Before recommending anything, we look at your full financial picture. A 32-year-old renter in Beltline needs a very different plan than a 50-year-old homeowner in Springbank Hill.",
      },
      {
        title: "Ongoing support",
        description:
          "New baby, new home, business change - we revisit your policy at key milestones so you're never underprotected.",
      },
    ],
  },
  options: {
    heading: "Alberta Life Insurance Options Available to Residents",
    description:
      "Every household has different priorities. Here's a plain-language look at the main options we help clients navigate.",
    cards: [
      {
        title: "Term Life Insurance",
        href: "/term-life-insurance",
        description:
          "Fixed coverage for 10, 20, or 30 years at a predictable monthly premium. The most affordable way to protect your income and mortgage during your peak earning years.",
      },
      {
        title: "Permanent Life Insurance",
        href: "/permanent-life-insurance",
        description:
          "Coverage that never expires, with a cash value component that grows over time. Better suited for estate planning, final expenses, or lifelong certainty.",
      },
      {
        title: "Mortgage Insurance",
        href: "/mortgage-insurance",
        description:
          "Designed to clear your outstanding mortgage balance if you pass away. For Calgary homeowners carrying significant debt, this keeps your family in their home.",
      },
      {
        title: "No Medical Life Insurance",
        href: "/no-medical-insurance",
        description:
          "No exam. No lab work. Approval often within days. If your health history has complicated previous applications, simplified and guaranteed issue options are more accessible than most people expect.",
      },
    ],
  },
  coverage: {
    heading: "How Much Life Insurance in Calgary Do You Actually Need?",
    description:
      "There's no universal number. The right amount depends on your income, debt, dependents, and lifestyle. Three methods help you land on a realistic figure.",
    methods: [
      {
        title: "Income Method",
        description:
          "Multiply your gross annual income by 10 to 12. Earning $85,000 means aiming for $850,000 to $1,020,000 in coverage - enough to replace your income across the years your family depends on it most.",
      },
      {
        title: "Debt and Mortgage Method",
        description:
          "Add every outstanding obligation - mortgage, vehicle loan, line of credit, student debt. In Calgary, mortgage balances alone regularly exceed $600,000. Coverage should account for the full picture.",
      },
      {
        title: "Lifestyle Method",
        description:
          "Multiply your family's monthly expenses by the years they'll depend on your income, then add future costs like post-secondary education. The result reflects what your family actually needs - not a rough estimate.",
      },
    ],
    conclusion:
      "Most families benefit from combining all three. Our life insurance broker in Calgary can walk through the full calculation with you and make sure the number reflects your actual obligations.",
  },
  why: {
    heading: "Why Clients Choose einsured.ca?",
    description:
      "Looking for the best life insurance in Alberta means finding coverage that genuinely fits your life - not just the cheapest number on a comparison site.",
    cards: [
      {
        title: "Digital-first",
        description:
          "No office visits. Everything happens online or by phone - evenings and weekends included. Most clients have coverage confirmed within days of their first consultation.",
      },
      {
        title: "Alberta-wide service",
        description:
          "We serve Calgary, Edmonton, Red Deer, Lethbridge, and all of Alberta. One licensed broker, wherever you are in the province.",
      },
      {
        title: "Multi-insurer comparison",
        description:
          "When you request life insurance quotes in Calgary through einsured.ca, you get real options across Canada's top carriers - not a single take-it-or-leave-it quote.",
      },
      {
        title: "No pressure, ever.",
        description:
          "No commission-driven push. No advisor who disappears after the policy is signed.",
      },
      {
        title: "Licensed and regulated",
        description:
          "Every advisor holds a valid licence and operates under the oversight of the Alberta Insurance Council - the provincial body that regulates insurance professionals across Alberta.",
      },
    ],
  },
  faqs: [
    {
      question: "How much does life insurance in Calgary typically cost?",
      answer:
        "Most healthy applicants pay between $20 and $60 per month for term coverage, depending on age, health, and coverage amount. einsured.ca compares across insurers to make sure you're not overpaying.",
    },
    {
      question: "Do I need a medical exam to get approved?",
      answer:
        "Not always. Many policies offer no-exam or simplified issue options. We confirm what applies to your profile before you apply - no surprises.",
    },
    {
      question: "How quickly can I get covered?",
      answer:
        "Most clients receive approval within a few days of their first consultation. No-exam policies can move even faster.",
    },
    {
      question: "What's the difference between a broker and an insurance company?",
      answer:
        "An insurer sells their own products. A life insurance broker in Calgary like einsured.ca works independently - comparing plans across multiple carriers to find what fits your profile and budget.",
    },
    {
      question: "Can I apply with a pre-existing condition?",
      answer:
        "In most cases, yes. Life insurance in Alberta is available through multiple carriers, each with different underwriting criteria. As an independent broker, we compare those approaches to find the most accessible terms for your situation.",
    },
    {
      question: "I have group coverage through work. Do I still need a personal policy?",
      answer:
        "Almost certainly. Group plans disappear if you leave or are laid off. A personal policy stays with you regardless of where you work and locks in your premium at your current age and health.",
    },
  ],
  finalCta: {
    heading: "Get Clear on Your Coverage Today",
    paragraphs: [
      "Your family's financial stability is worth getting right.",
      "At einsured.ca, we help Calgary residents navigate Alberta life insurance entirely online - no office visit, no obligation. Licensed advisors, real comparisons, straight answers.",
    ],
  },
};

const LifeInsuranceCalgary = () => (
  <LocationLifeInsurancePage data={calgaryData} />
);

export default LifeInsuranceCalgary;